import api from './api';
import { Usuario } from '../types';

interface LoginResponse {
  success: boolean;
  message: string;
  token: string;
  usuario: Usuario;
}

interface ApiResponse {
  success: boolean;
  message: string;
  usuario?: Usuario;
}

export const authService = {
  /**
   * Realiza o login do usuário na API
   * @param email Email do usuário
   * @param senha Senha do usuário
   * @returns Promise com resultado do login
   */
  login: async (email: string, senha: string): Promise<{ success: boolean; usuario?: Usuario; token?: string; message?: string }> => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', { email, senha });
      
      if (response.data.success) {
        const usuarioLogin = {
          ...response.data.usuario,
          avatar: response.data.usuario.avatar || '/profilepicture.png'
        };

        localStorage.setItem('bravobet_token', response.data.token);
        localStorage.setItem('bravobet_usuario', JSON.stringify(usuarioLogin));

        return {
          success: true,
          usuario: usuarioLogin,
          token: response.data.token
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Erro ao fazer login'
        };
      }
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao conectar com o servidor'
      };
    }
  },

  /**
   * Verifica se o token JWT é válido
   * @returns Promise com resultado da verificação
   */
  verificarToken: async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('bravobet_token');
      
      if (!token) {
        console.log('Token não encontrado no localStorage');
        return false;
      }
      
      // Verificar se o token é válido no backend
      const response = await api.post<ApiResponse>('/auth/verificar', { token });
      
      if (response.data.success && response.data.usuario) {
        // Atualizar os dados do usuário no localStorage
        localStorage.setItem('bravobet_usuario', JSON.stringify(response.data.usuario));
        return true;
      } else {
        console.log('Token inválido ou expirado');
        return false;
      }
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      // Limpar dados de autenticação em caso de erro
      localStorage.removeItem('bravobet_token');
      localStorage.removeItem('bravobet_usuario');
      return false;
    }
  },

  /**
   * Realiza o logout do usuário
   */
  logout: (): void => {
    localStorage.removeItem('bravobet_token');
    localStorage.removeItem('bravobet_usuario');
  },

  /**
   * Obtém os dados do usuário atual
   * @returns Promise com os dados do usuário
   */
  obterUsuarioAtual: async (): Promise<{ success: boolean; usuario?: Usuario; message?: string }> => {
    try {
      const token = localStorage.getItem('bravobet_token');
      
      if (!token) {
        return { success: false, message: 'Token não encontrado' };
      }
      
      // Usar o endpoint /auth/me para obter os dados do usuário atual
      const response = await api.get<{ success: boolean; usuario: Usuario }>('/auth/me');
      
      if (response.data.success) {
        const usuarioAtual = {
          ...response.data.usuario,
          avatar: response.data.usuario.avatar || '/profilepicture.png'
        };

        localStorage.setItem('bravobet_usuario', JSON.stringify(usuarioAtual));

        return {
          success: true,
          usuario: usuarioAtual
        };
      } else {
        return {
          success: false,
          message: 'Erro ao obter dados do usuário'
        };
      }
    } catch (error: any) {
      console.error('Erro ao obter dados do usuário:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erro ao conectar com o servidor'
      };
    }
  },
  
  /**
   * Atualiza o perfil do usuário
   * @param dadosAtualizados Dados a serem atualizados
   * @returns Promise com resultado da atualização
   */
  atualizarPerfil: async (dadosAtualizados: Partial<Usuario>): Promise<boolean> => {
    try {
      const token = localStorage.getItem('bravobet_token');
      
      if (!token) {
        console.error('Token não encontrado');
        return false;
      }
      
      const response = await api.put<ApiResponse>('/auth/perfil', dadosAtualizados);
      
      if (response.data.success && response.data.usuario) {
        // Atualizar os dados do usuário no localStorage
        localStorage.setItem('bravobet_usuario', JSON.stringify(response.data.usuario));
        return true;
      }
      
      console.error('Erro ao atualizar perfil:', response.data.message);
      return false;
    } catch (error: any) {
      console.error('Erro ao atualizar perfil:', error);
      return false;
    }
  }
};

export default authService;
